import { useCallback, useEffect, useState } from "react";
import { listAbsenceRequests, type EmployeeAbsenceRequest } from "@/lib/employeeAbsences";
import {
  getActiveTimer,
  getEmployeeAccountSummary,
  getEmployeeMonthSummary,
  type EmployeeAccountSummary,
  type EmployeeTimeMonthSummary,
  type EmployeeTimeTimer,
} from "@/lib/employeeTimes";
import { useRealtimeSync } from "@/lib/useRealtime";

export interface PersonalSelfData {
  account: EmployeeAccountSummary | null;
  month: EmployeeTimeMonthSummary | null;
  absences: EmployeeAbsenceRequest[];
  timer: EmployeeTimeTimer | null;
}

const EMPTY: PersonalSelfData = { account: null, month: null, absences: [], timer: null };

export function dedupeAbsencesById(list: EmployeeAbsenceRequest[]): EmployeeAbsenceRequest[] {
  const seen = new Set<number>();
  return list.filter((req) => {
    if (seen.has(req.id)) return false;
    seen.add(req.id);
    return true;
  });
}

async function fetchOwnAbsences(
  years: number[],
  login: string,
): Promise<EmployeeAbsenceRequest[]> {
  const lists = await Promise.all(
    years.map((year) => listAbsenceRequests({ user_login: login, year })),
  );
  return dedupeAbsencesById(lists.flat());
}

/**
 * Eigene Kennzahlen für die Personal-Startseite: Stundenkonto, laufender Monat,
 * eigene Abwesenheitsanträge und aktiver Timer. Einzelne Fehler blenden nur den
 * jeweiligen Wert aus, statt die ganze Seite zu blockieren.
 */
export function usePersonalSelfData(login: string) {
  const [data, setData] = useState<PersonalSelfData>(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      // Ohne Login (Karten noch nicht aufgelöst) im Ladezustand bleiben,
      // damit keine „—“-Platzhalter vor den echten Werten aufblitzen.
      if (!login) return;
      if (!opts?.silent) setLoading(true);
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const absenceYears = month >= 11 ? [year, year + 1] : [year];
      const [account, monthSummary, ownAbsences, timer] = await Promise.allSettled([
        getEmployeeAccountSummary({ user_login: login, year }),
        getEmployeeMonthSummary({ user_login: login, year, month }),
        fetchOwnAbsences(absenceYears, login),
        getActiveTimer(),
      ]);
      setData({
        account: account.status === "fulfilled" ? account.value : null,
        month: monthSummary.status === "fulfilled" ? monthSummary.value : null,
        absences: ownAbsences.status === "fulfilled" ? ownAbsences.value : [],
        timer: timer.status === "fulfilled" ? timer.value : null,
      });
      setLoading(false);
    },
    [login],
  );

  useEffect(() => {
    void load();
  }, [load]);

  useRealtimeSync({
    domains: ["employee-times"],
    enabled: Boolean(login),
    onChange: () => void load({ silent: true }),
  });

  return { data, loading, reload: () => void load({ silent: true }) };
}
